import { CURATOR_NOTE_MAX, fmtDayTime } from '@remote-medium/shared';
import { Button, Textarea } from '@remote-medium/ui';
import { Lock, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useCurrentCurator } from './current-curator.tsx';
import { Card, ConfirmButton, SectionHeader } from './ui.tsx';

interface Note {
  id: string;
  body: string;
  createdBy: string;
  byName: string;
  at: string;
}

// Internal notes on a candidate or a company. Never edited: each note is kept
// with its author and time, newest first; its author may delete it (Q61).
export function CuratorNotes({
  notes,
  hiddenFrom,
  placeholder,
  busy,
  onAdd,
  onDelete,
}: {
  notes: Note[];
  /** Who never sees them: "companies or the candidate", "the company". */
  hiddenFrom: string;
  placeholder: string;
  busy: boolean;
  onAdd: (body: string) => Promise<boolean>;
  /** Throws on failure; the confirm dialog shows the error. */
  onDelete: (noteId: string) => Promise<unknown>;
}) {
  const me = useCurrentCurator();
  const [draft, setDraft] = useState('');

  const add = async () => {
    const body = draft.trim();
    if (!body) return;
    if (await onAdd(body)) setDraft('');
  };

  return (
    <Card>
      <div className="flex flex-col gap-1">
        <SectionHeader title="Curator notes" />
        <p className="m-0 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock aria-hidden="true" className="size-3.5" />
          Internal. Never shown to {hiddenFrom}.
        </p>
      </div>

      <Textarea
        rows={3}
        maxLength={CURATOR_NOTE_MAX}
        aria-label="Add a curator note"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          Kept with your name and the date. Emails and phone numbers are removed.
        </span>
        <Button size="sm" disabled={busy || !draft.trim()} onClick={() => void add()}>
          {busy ? 'Saving…' : 'Add note'}
        </Button>
      </div>

      {notes.length > 0 && (
        <ul className="m-0 flex list-none flex-col border-t border-border p-0">
          {notes.map((n) => (
            <li
              key={n.id}
              className="flex flex-col gap-1 border-t border-border py-3 first:border-t-0 last:pb-0"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground">
                  {n.byName} · {fmtDayTime(n.at)}
                </span>
                {n.createdBy === me?.id && (
                  <ConfirmButton
                    variant="ghost"
                    title="Delete this note?"
                    description="It is removed for every curator. This can't be undone."
                    confirmLabel="Delete note"
                    destructive
                    onConfirm={() => onDelete(n.id)}
                  >
                    <Trash2 aria-hidden="true" />
                    <span className="sr-only">Delete note</span>
                  </ConfirmButton>
                )}
              </div>
              <p className="m-0 text-sm leading-6 whitespace-pre-line">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
